/**
 * InterpretationSection Component
 * Displays a single interpretation section card
 */

import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Info, AlertTriangle, HelpCircle, Star } from 'lucide-react';
import ConfidenceBadge from './ConfidenceBadge';

const InterpretationSection = ({
  section,
  index,
  lang = 'tr',
  isHighlighted,
  hasLineMapping,
  SectionIcon,
  title,
  onSectionClick,
  onExplainabilityClick
}) => {
  const sectionId = section.id || 'unknown';
  const IconComponent = SectionIcon || Star;
  
  // Phase 3.6: Has explainability?
  const hasExplainability = section.explainability || 
    (section.status === 'limited' && section.limitations?.length > 0);
  
  return (
    <motion.div
      key={sectionId}
      className={`interpretation-card interpretation-${section.status} ${isHighlighted ? 'line-highlighted' : ''}`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2 + index * 0.1 }}
      data-testid={`palm-section-${sectionId}`}
      data-section={sectionId}
      onClick={() => hasLineMapping && onSectionClick(sectionId)}
      style={{ cursor: hasLineMapping ? 'pointer' : 'default' }}
    >
      <div className="interpretation-card-header">
        <IconComponent size={20} className="section-icon" />
        <h3>{title}</h3>
        
        {/* Phase 3.7: Confidence Badge */}
        {section.status === 'generated' && section.confidence > 0 && (
          <ConfidenceBadge 
            confidence={section.confidence}
            confidenceDetails={section.confidence_details}
            lang={lang}
          />
        )}
      </div>
      
      {section.status === 'generated' && section.interpretation && (
        <div className="interpretation-content">
          <p>{section.interpretation}</p>
          {section.guidance && (
            <div className="interpretation-guidance">
              <Info size={16} />
              <span>{section.guidance}</span>
            </div>
          )}
          
          {/* Phase 3.6: Explainability Button */}
          {hasExplainability && (
            <button
              className="explainability-btn"
              onClick={(e) => {
                e.stopPropagation();
                onExplainabilityClick(section);
              }}
              data-testid={`explainability-btn-${sectionId}`}
              aria-label={lang === 'tr' ? 'Neden böyle yorumlandı?' : 'Why was it interpreted this way?'}
            >
              <HelpCircle size={14} />
              {lang === 'tr' ? 'Neden?' : 'Why?'}
            </button>
          )}
        </div>
      )}
      
      {section.status === 'limited' && (
        <div className="interpretation-limited" data-testid="palm-section-limited">
          <AlertTriangle size={16} />
          <p>
            {lang === 'tr' 
              ? 'Bu bölüm için görüntüde yeterli güvenilir detay bulunamadı.'
              : 'Insufficient reliable detail was found in the image for this section.'
            }
          </p>
          {section.limitations?.length > 0 && (
            <ul className="limitation-list">
              {section.limitations.map((lim, i) => (
                <li key={i}>{lim}</li>
              ))}
            </ul>
          )}
          {/* Phase 3.6: Explainability for limited sections */}
          {hasExplainability && (
            <button
              className="explainability-btn explainability-limited"
              onClick={(e) => {
                e.stopPropagation();
                onExplainabilityClick(section);
              }}
              data-testid={`explainability-btn-${sectionId}`}
            >
              <HelpCircle size={14} />
              {lang === 'tr' ? 'Neden sınırlı?' : 'Why limited?'}
            </button>
          )}
        </div>
      )}
      
      {section.status === 'unavailable' && (
        <div className="interpretation-unavailable">
          <p>
            {lang === 'tr'
              ? 'Bu bölüm mevcut pakette bulunmuyor.'
              : 'This section is not available in the current package.'
            }
          </p>
        </div>
      )}
    </motion.div>
  );
};

InterpretationSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    interpretation: PropTypes.string,
    guidance: PropTypes.string,
    confidence: PropTypes.number,
    confidence_details: PropTypes.object,
    explainability: PropTypes.string,
    limitations: PropTypes.array,
    source_features: PropTypes.array
  }).isRequired,
  index: PropTypes.number.isRequired,
  lang: PropTypes.string,
  isHighlighted: PropTypes.bool,
  hasLineMapping: PropTypes.bool,
  SectionIcon: PropTypes.elementType,
  title: PropTypes.string.isRequired,
  onSectionClick: PropTypes.func.isRequired,
  onExplainabilityClick: PropTypes.func.isRequired
};

export default InterpretationSection;
